import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, ArrowLeft, ArrowRight, CheckCircle, Target,
  Briefcase, DollarSign, Clock, TrendingUp, Sparkles, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { getAuthToken } from '@/utils/secureStorage';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SkillsAssessment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const fetchQuiz = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/workforce/skills-assessment`);
      if (res.ok) {
        setQuiz(await res.json());
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  const handleAnswer = (questionId, answerId) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please log in to save your results');
      return;
    }

    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/skills-assessment/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          answers: Object.entries(answers).map(([question_id, answer_id]) => ({
            question_id,
            answer_id
          }))
        })
      });

      if (res.ok) {
        const data = await res.json();
        setResults(data);
        toast.success('Assessment complete!');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (err) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setResults(null);
    setCurrentQuestion(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Results View
  if (results) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50/30" data-testid="assessment-results">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <Sparkles className="h-16 w-16 mx-auto mb-4 text-yellow-300" />
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Career Matches</h1>
            <p className="text-green-100">Based on your responses, here are your top sectors</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="space-y-6">
            {results.recommendations?.map((rec, idx) => (
              <motion.div
                key={rec.sector_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.2 }}
              >
                <Card className={`overflow-hidden ${idx === 0 ? 'border-2 border-green-400 shadow-lg' : ''}`}>
                  {idx === 0 && (
                    <div className="bg-green-500 text-white px-4 py-2 text-center text-sm font-medium">
                      Top Match
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{rec.sector_name}</CardTitle>
                        <CardDescription>{rec.description}</CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{rec.match_score}%</div>
                        <p className="text-sm text-slate-500">Match</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-slate-500">Starting Wage</p>
                          <p className="font-semibold">${rec.avg_starting_wage}/hr</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-slate-500">Time to Credential</p>
                          <p className="font-semibold">{rec.time_to_credential} weeks</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-sm text-slate-500">Local Demand</p>
                          <p className="font-semibold capitalize">{rec.demand}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-slate-500 mb-2">Sample Roles</p>
                      <div className="flex flex-wrap gap-2">
                        {rec.roles?.map(role => (
                          <Badge key={role} variant="secondary">{role}</Badge>
                        ))}
                      </div>
                    </div>

                    {rec.career_ladder && (
                      <div className="text-sm text-slate-600">
                        <p className="font-medium mb-1">Career Path:</p>
                        <p>{rec.career_ladder}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button variant="outline" onClick={handleRetake}>
              <RotateCcw className="h-4 w-4 mr-2" /> Retake Assessment
            </Button>
            <Link to="/workforce/assessment">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Start Enrollment <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Quiz View
  const question = quiz?.questions?.[currentQuestion];
  const progress = quiz ? ((currentQuestion + 1) / quiz.total_questions) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50/30" data-testid="skills-assessment">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-8">
        <div className="container mx-auto px-4">
          <Link to="/workforce" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workforce Hub
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <ClipboardCheck className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Skills Assessment</h1>
              <p className="text-blue-100">Find your best-fit career path</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="container mx-auto px-4 py-4 max-w-2xl">
        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
          <span>Question {currentQuestion + 1} of {quiz?.total_questions || 0}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question Card */}
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <AnimatePresence mode="wait">
          {question && (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">{question.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup 
                    value={answers[question.id] || ''} 
                    onValueChange={(value) => handleAnswer(question.id, value)}
                  >
                    <div className="space-y-3">
                      {question.answers.map((answer) => (
                        <div 
                          key={answer.id}
                          className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            answers[question.id] === answer.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleAnswer(question.id, answer.id)}
                        >
                          <RadioGroupItem value={answer.id} id={answer.id} />
                          <Label htmlFor={answer.id} className="flex-1 cursor-pointer">
                            {answer.text}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>

          {currentQuestion === (quiz?.total_questions || 0) - 1 ? (
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !user}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? 'Analyzing...' : 'Get My Results'}
              <Sparkles className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentQuestion(Math.min((quiz?.total_questions || 0) - 1, currentQuestion + 1))}
              disabled={!answers[question?.id]}
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Question Dots */}
        <div className="flex justify-center gap-1 mt-6">
          {quiz?.questions?.map((_, idx) => (
            <button
              key={`item-${idx}`}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentQuestion 
                  ? 'bg-blue-600 scale-125' 
                  : answers[quiz.questions[idx]?.id] 
                    ? 'bg-green-500' 
                    : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {!user && (
          <p className="text-center text-sm text-slate-500 mt-4">
            <Link to="/login" className="text-blue-600 hover:underline">Log in</Link> to save your results
          </p>
        )}
      </div>
    </div>
  );
}
